// Minimal stand-in for Foundation's Gregorian `Calendar`, used where the Swift engines take a `calendar:` parameter
// (MultiDayPlanner day starts, RegulationResolver "night" and season checks). Not a port of a TrailBivvyKit file.
//
// Three flavours: the device's local time (the Swift default `Calendar(identifier: .gregorian)`), UTC, and any IANA
// time zone (through `Intl`, e.g. "Europe/Zurich" to plan in the trail's own time zone whatever the device says).

import type { EpochMs, SeasonalWindow } from '../core/models';

export interface DateComponents {
  year: number;
  /** 1…12 */
  month: number;
  /** 1…31 */
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export interface Calendar {
  /** 'local', 'UTC' or an IANA zone name. */
  readonly timeZone: string;
  /** Calendar components of an instant in this calendar's time zone. */
  components(date: EpochMs): DateComponents;
  /** First instant of the day containing `date` (Swift `startOfDay(for:)`). */
  startOfDay(date: EpochMs): EpochMs;
  /** Same wall-clock time `days` calendar days later (Swift `date(byAdding: .day, value:to:)`). */
  addingDays(days: number, date: EpochMs): EpochMs;
  /** `hour:minute:second` on the day of `date` (Swift `date(bySettingHour:minute:second:of:)`). */
  settingTime(date: EpochMs, hour: number, minute?: number, second?: number): EpochMs;
}

/** The device's local time zone (the Swift default calendar). */
export const localCalendar: Calendar = {
  timeZone: 'local',
  components(date) {
    const d = new Date(date);
    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      hour: d.getHours(),
      minute: d.getMinutes(),
      second: d.getSeconds(),
    };
  },
  startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  },
  addingDays(days, date) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d.getTime();
  },
  settingTime(date, hour, minute = 0, second = 0) {
    const d = new Date(date);
    d.setHours(hour, minute, second, 0);
    return d.getTime();
  },
};

export const utcCalendar: Calendar = {
  timeZone: 'UTC',
  components(date) {
    const d = new Date(date);
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
      hour: d.getUTCHours(),
      minute: d.getUTCMinutes(),
      second: d.getUTCSeconds(),
    };
  },
  startOfDay(date) {
    const d = new Date(date);
    d.setUTCHours(0, 0, 0, 0);
    return d.getTime();
  },
  addingDays(days, date) {
    const d = new Date(date);
    d.setUTCDate(d.getUTCDate() + days);
    return d.getTime();
  },
  settingTime(date, hour, minute = 0, second = 0) {
    const d = new Date(date);
    d.setUTCHours(hour, minute, second, 0);
    return d.getTime();
  },
};

const zoned = new Map<string, Calendar>();

/** Calendar of an IANA time zone ('UTC' and 'local' return the built-in calendars). Throws for an unknown zone. */
export function calendarForTimeZone(timeZone: string): Calendar {
  if (timeZone === 'local') return localCalendar;
  if (timeZone === 'UTC') return utcCalendar;
  const cached = zoned.get(timeZone);
  if (cached) return cached;
  const calendar = makeZonedCalendar(timeZone);
  zoned.set(timeZone, calendar);
  return calendar;
}

function makeZonedCalendar(timeZone: string): Calendar {
  const format = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  });
  const components = (date: EpochMs): DateComponents => {
    const c: DateComponents = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
    for (const part of format.formatToParts(new Date(date))) {
      if (part.type in c) c[part.type as keyof DateComponents] = Number(part.value);
    }
    return c;
  };
  // Offset (ms) of the zone at an instant, at second resolution.
  const offset = (date: EpochMs): number => {
    const c = components(date);
    const wholeSeconds = date - (((date % 1000) + 1000) % 1000);
    return Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second) - wholeSeconds;
  };
  // Instant of a wall-clock time (day overflow allowed, as in Date.UTC). The second pass uses the offset in force at
  // the first guess, which settles days on which the offset changes (DST).
  const instant = (year: number, month: number, day: number, hour: number, minute: number, second: number) => {
    const wall = Date.UTC(year, month - 1, day, hour, minute, second);
    return wall - offset(wall - offset(wall));
  };
  return {
    timeZone,
    components,
    startOfDay(date) {
      const c = components(date);
      return instant(c.year, c.month, c.day, 0, 0, 0);
    },
    addingDays(days, date) {
      const c = components(date);
      const milliseconds = ((date % 1000) + 1000) % 1000;
      return instant(c.year, c.month, c.day + days, c.hour, c.minute, c.second) + milliseconds;
    },
    settingTime(date, hour, minute = 0, second = 0) {
      const c = components(date);
      return instant(c.year, c.month, c.day, hour, minute, second);
    },
  };
}

/**
 * `SeasonalWindow.contains(_:calendar:)` with an explicit calendar (inclusive; windows may wrap the year end).
 * Same rule as `seasonContains` in src/core/domain.ts, which always uses the device's local time.
 */
export function seasonContainsOn(window: SeasonalWindow, date: EpochMs, calendar: Calendar): boolean {
  const c = calendar.components(date);
  const value = c.month * 100 + c.day;
  const start = window.startMonth * 100 + window.startDay;
  const end = window.endMonth * 100 + window.endDay;
  if (start <= end) return value >= start && value <= end;
  return value >= start || value <= end;
}
